import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search, RotateCcw, ShoppingCart, Plus, Minus, ExternalLink, Copy, Check, Key, Package, X, Share2 } from 'lucide-react'
import type { Product, ProductVariant, ProductCategory } from '../../../lib/types'
import { useAuth } from '../../auth/hooks/useAuth'
import { sendTelegramNotification } from '../../../lib/telegram'
import { apiGetJson, apiPostJson } from '../../../lib/apiClient'
import AuthModal from '../../auth/components/AuthModal'
import { useDiscounts } from '../hooks/useDiscounts'
import { removeVietnameseTones } from '../../../lib/text'
import { unwrapFirst, unwrapList } from '../../../lib/apiUnwrap'
import { useCustomerLeftSidebarSlot } from '../../../layouts/customerLeftSidebarSlot'
import { useToast } from '../../../lib/toast'

import ProductCard from '../components/ProductCard'
import ProductsUnifiedPanel from '../components/ProductsUnifiedPanel'

import { extractSiteStatsFromApiResponse, type SiteStats } from '../utils/siteStats'
import { buildProductShareLink } from '../utils/productShare'

type ComboItem = {
  id: string
  combo_id: string
  variant_id: string
  quantity: number
  variant_name?: string
  variant_short_name?: string | null
  variant_price?: number
}

type VariantCombo = {
  id: string
  base_variant_id: string
  combo_name: string
  required_points: number
  is_active: boolean
  sort_order: number
  items: ComboItem[]
}

const normalizeText = (value?: string | null): string => {
  const text = String(value ?? '').trim()
  if (!text) return ''
  const lowered = text.toLowerCase()
  if (lowered === 'null' || lowered === 'undefined') return ''
  return text
}

const resolveProductName = (product: Product): string => {
  const byName = normalizeText(product.name)
  if (byName) return byName

  const byCode = normalizeText(product.product_code)
  if (byCode) return byCode

  for (const variant of product.variants || []) {
    const byShortName = normalizeText(variant.short_name)
    if (byShortName) return byShortName
    const byVariantName = normalizeText(variant.name)
    if (byVariantName) return byVariantName
  }

  return 'Sản phẩm'
}

export default function ProductsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, refreshProfile } = useAuth()
  const { toast } = useToast()
  const { setLeftSidebar, clearLeftSidebar } = useCustomerLeftSidebarSlot()
  const allowCategoryReorder = false
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<ProductCategory[]>([])
  const [favoriteProductIds, setFavoriteProductIds] = useState<Set<string>>(() => new Set())
  const [favoritesLoading, setFavoritesLoading] = useState(false)
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)

  const [siteStats, setSiteStats] = useState<SiteStats | null>(null)
  const [siteStatsLoading, setSiteStatsLoading] = useState(false)
  const [siteStatsError, setSiteStatsError] = useState<string | null>(null)
  const [activeProductId, setActiveProductId] = useState<string | null>(null)
  const productElsRef = useRef<Record<string, HTMLDivElement | null>>({})
  const highlightTimersRef = useRef<number[]>([])
  const lastHighlightedElRef = useRef<HTMLElement | null>(null)
  const handledSharedProductRef = useRef<string | null>(null)

  void siteStats
  void siteStatsLoading
  void siteStatsError
  const productsForToc = useMemo(() => filteredProducts, [filteredProducts])
  const fixedCategoryOrder = useMemo(() => ['software', 'game', 'education', 'programming', 'social', 'creation', 'vehicle'], [])
  const availableCategoryOrder = useMemo(() => {
    const current = Array.from(new Set(productsForToc.map((p) => p.category).filter(Boolean))) as string[]
    const db = (categories || []).map((c) => c.key).filter(Boolean)

    const base = db.length ? db : fixedCategoryOrder
    const extras = current.filter((c) => !base.includes(c))
    return [...base, ...extras]
  }, [productsForToc, categories, fixedCategoryOrder])
  const [categoryOrder, setCategoryOrder] = useState<string[]>(() => [])
  const [tocDraggingCategory, setTocDraggingCategory] = useState<string | null>(null)
  const [tocOverCategoryReorder, setTocOverCategoryReorder] = useState<string | null>(null)
  const [tocSearch, setTocSearch] = useState('')
  const [isTocOpen, setIsTocOpen] = useState(false)
  const productMatchesQuery = useCallback((p: Product, raw: string) => {
    const q = removeVietnameseTones((raw || '').trim())
    if (!q) return true

    const haystacks: string[] = []
    if (p?.name) haystacks.push(p.name)
    if (p?.product_code) haystacks.push(p.product_code)
    if (p?.mechanism) haystacks.push(p.mechanism)
    if (p?.recommended_model) haystacks.push(p.recommended_model)

    const variants = (p.variants || []) as ProductVariant[]
    for (const v of variants) {
      if (!v || v.is_archived) continue
      if (v.name) haystacks.push(v.name)
      if (v.short_name) haystacks.push(v.short_name)
    }

    return haystacks.some((s) => removeVietnameseTones(s || '').includes(q))
  }, [])
  const filteredProductsForToc = useMemo(() => {
    if (!tocSearch.trim()) return productsForToc
    return productsForToc.filter((p) => productMatchesQuery(p, tocSearch))
  }, [productsForToc, tocSearch, productMatchesQuery])
  const visibleCategoryOrder = useMemo(() => {
    const base = allowCategoryReorder && categoryOrder.length ? categoryOrder : availableCategoryOrder
    const merged: string[] = [...base]
    for (const c of availableCategoryOrder) {
      if (!merged.includes(c)) merged.push(c)
    }
    return merged.filter((c) => filteredProductsForToc.some((p) => p.category === c))
  }, [allowCategoryReorder, categoryOrder, availableCategoryOrder, filteredProductsForToc])
  const [filters, setFilters] = useState({
    category: 'all',
    search: '',
  })

  // Debounce search to prevent lag
  const [searchInput, setSearchInput] = useState('')
  const filtersSearchRef = useRef('')
  const isTypingRef = useRef(false) // Flag to track if user is actively typing

  useEffect(() => {
    filtersSearchRef.current = filters.search
  }, [filters.search])

  // Handler for search input change - sets typing flag
  const handleSearchInputChange = useCallback((value: string) => {
    isTypingRef.current = true
    setSearchInput(value)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) return prev
        return { ...prev, search: searchInput }
      })
      // Clear typing flag after debounce completes
      isTypingRef.current = false
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    // Skip URL -> Input sync if user is actively typing
    if (isTypingRef.current) return

    const params = new URLSearchParams(location.search)
    const q = (params.get('q') || '').trim()
    const current = (filtersSearchRef.current || '').trim()

    if (q === current) return
    setSearchInput(q)
    setFilters((prev) => {
      if (prev.search === q) return prev
      return { ...prev, search: q }
    })
  }, [location.search])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const currentQ = (params.get('q') || '').trim()
    const nextQ = filters.search.trim()

    if (currentQ === nextQ) return

    if (nextQ) {
      params.set('q', nextQ)
    } else {
      params.delete('q')
    }

    const search = params.toString()
    navigate({ pathname: '/products', search: search ? `?${search}` : '' }, { replace: true })
  }, [filters.search, location.search, navigate])

  const tocCategoryKeys = useMemo(() => {
    if (user && (favoriteProductIds.size > 0 || filters.category === 'favorites')) return ['favorites', ...visibleCategoryOrder]
    return visibleCategoryOrder
  }, [user, favoriteProductIds.size, visibleCategoryOrder, filters.category])
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null)
  const [selectedProductGuideUrl, setSelectedProductGuideUrl] = useState<string | undefined>(undefined)
  const [selectedProductName, setSelectedProductName] = useState('')
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [purchasing, setPurchasing] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [purchaseResult, setPurchaseResult] = useState<{
    keys: string[],
    giftKeys?: string[],
    guideUrl?: string,
    productName?: string,
    variantName?: string,
    comboName?: string,
    comboQuantity?: number,
    manualDelivery?: boolean
  } | null>(null)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [copiedShareProductId, setCopiedShareProductId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'full' | 'compact'>(() => {
    const saved = localStorage.getItem('taphoanoidungso-products-view')
    return saved === 'compact' ? 'compact' : 'full'
  })
  const [variantsModalProduct, setVariantsModalProduct] = useState<Product | null>(null)
  const [hoverPreview, setHoverPreview] = useState<{ text: string; x: number; y: number } | null>(null)
  const { computePercent, getUnitPrice: calcUnitPrice } = useDiscounts()

  const [combosLoading, setCombosLoading] = useState(false)
  const [variantCombos, setVariantCombos] = useState<VariantCombo[]>([])
  const [selectedComboId, setSelectedComboId] = useState<string>('')
  const [selectedComboQuantity, setSelectedComboQuantity] = useState<number>(0)

  const legacyCategoryLabel = (category: string | null | undefined) => {
    switch (category) {
      case 'software':
        return 'Phần mềm'
      case 'game':
        return 'Game'
      case 'education':
        return 'Giáo dục'
      case 'programming':
        return 'Lập trình'
      case 'social':
        return 'Mạng xã hội'
      case 'creation':
      case 'vehicle':
        return 'Sáng tạo nội dung'
      default:
        return category || '-'
    }
  }

  const categoryLabelMap = useMemo(() => {
    const m = new Map<string, string>()
      ; (categories || []).forEach((c) => {
        if (c?.key) m.set(c.key, c.label || c.key)
      })
    return m
  }, [categories])

  const getCategoryLabel = useCallback((category: string | null | undefined) => {
    if (!category) return '-'
    return categoryLabelMap.get(category) || legacyCategoryLabel(category)
  }, [categoryLabelMap])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const category = params.get('category')
    if (!category) return

    const defaultAllowed = ['all', 'favorites', 'software', 'game', 'education', 'programming', 'social', 'creation', 'vehicle']
    const allowed = new Set<string>([...defaultAllowed, ...(categories || []).map((c) => c.key)])
    const nextCategory = allowed.has(category) ? category : 'all'

    setFilters((prev) => {
      if (prev.category === nextCategory) return prev
      return { ...prev, category: nextCategory }
    })
  }, [location.search, categories])

  useEffect(() => {
    if (!allowCategoryReorder) {
      setCategoryOrder([])
      return
    }
    setCategoryOrder((prev) => {
      const base = prev.length ? prev : []
      const merged: string[] = [...base]
      for (const c of availableCategoryOrder) {
        if (!merged.includes(c)) merged.push(c)
      }
      return merged
    })
  }, [availableCategoryOrder, allowCategoryReorder])

  const fetchCategories = useCallback(async () => {
    try {
      const resp = await apiGetJson<{ data: ProductCategory[] | null }>('/api/categories')
      setCategories((resp?.data || []) as ProductCategory[])
    } catch (error) {
      console.error('Failed to fetch categories:', error)
      setCategories([])
    }
  }, [])

  const persistCategoryOrder = useCallback(
    async (nextOrder: string[]) => {
      void nextOrder
      return
    },
    []
  )

  useEffect(() => {
    void fetchCategories()
  }, [fetchCategories])

  useEffect(() => {
    let alive = true

    const run = async () => {
      try {
        setSiteStatsLoading(true)
        setSiteStatsError(null)
        const resp = await apiGetJson<any>('/api/site-stats')
        const stats = extractSiteStatsFromApiResponse(resp)
        if (!alive) return
        setSiteStats(stats)
      } catch (e: any) {
        if (!alive) return
        setSiteStats(null)
        setSiteStatsError(typeof e?.message === 'string' ? e.message : 'Không thể tải thống kê site')
      } finally {
        if (alive) setSiteStatsLoading(false)
      }
    }

    void run()
    return () => {
      alive = false
    }
  }, [])

  const applyFilters = useCallback(() => {
    let filtered = products

    filtered = filtered.filter((p) => !(p as any).is_archived)

    if (filters.category === 'favorites') {
      filtered = filtered.filter(p => favoriteProductIds.has(p.id))
    } else if (filters.category !== 'all') {
      filtered = filtered.filter(p => p.category === filters.category)
    }

    if (filters.search) {
      filtered = filtered.filter((p) => productMatchesQuery(p, filters.search))
    }

    // Sort variants within each product based on sortBy filter
    // If a product has variants but all are archived => hide product from customer.
    filtered = filtered
      .map((product) => {
        const rawVariants = (product.variants || []) as ProductVariant[]
        const visibleVariants = rawVariants.filter(v => !(v as any).is_archived)

        if (rawVariants.length > 0 && visibleVariants.length === 0) return null
        if (visibleVariants.length === 0) return { ...product, variants: [] }

        const sortedVariants = [...visibleVariants]

        const strategy = product.variant_sort_strategy || 'default'

        if (strategy === 'price_asc') {
          sortedVariants.sort((a, b) => a.price - b.price)
        } else if (strategy === 'price_desc') {
          sortedVariants.sort((a, b) => b.price - a.price)
        } else if (strategy === 'duration_asc') {
          sortedVariants.sort((a, b) => (a.duration_days || 0) - (b.duration_days || 0))
        } else if (strategy === 'duration_desc') {
          sortedVariants.sort((a, b) => (b.duration_days || 0) - (a.duration_days || 0))
        } else if (strategy === 'bestselling') {
          sortedVariants.sort((a, b) => (b.total_sold || 0) - (a.total_sold || 0))
        } else if (strategy === 'stock_asc') {
          sortedVariants.sort((a, b) => (a.stock || 0) - (b.stock || 0))
        } else if (strategy === 'stock_desc') {
          sortedVariants.sort((a, b) => (b.stock || 0) - (a.stock || 0))
        } else {
          sortedVariants.sort((a, b) => {
            if ((a.sort_order || 0) !== (b.sort_order || 0)) {
              return (a.sort_order || 0) - (b.sort_order || 0)
            }
            return a.price - b.price
          })
        }

        return { ...product, variants: sortedVariants }
      })
      .filter(Boolean) as Product[]

    setFilteredProducts(filtered)
  }, [products, filters, productMatchesQuery, favoriteProductIds])

  useEffect(() => {
    fetchProducts()
  }, [])

  useEffect(() => {
    const loadFavorites = async () => {
      if (!user?.id) {
        setFavoriteProductIds(new Set())
        return
      }
      setFavoritesLoading(true)
      try {
        const resp = await apiGetJson<any>('/api/favorites')
        const ids = unwrapList<string>(resp)
        setFavoriteProductIds(new Set(ids.map((x) => String(x))))
      } catch (err) {
        console.error('Failed to load favorites:', err)
        setFavoriteProductIds(new Set())
      } finally {
        setFavoritesLoading(false)
      }
    }

    void loadFavorites()
  }, [user?.id])

  const toggleFavorite = async (productId: string) => {
    if (!user?.id) {
      setShowAuthModal(true)
      return
    }

    const isFav = favoriteProductIds.has(productId)
    setFavoriteProductIds((prev) => {
      const next = new Set(prev)
      if (isFav) next.delete(productId)
      else next.add(productId)
      return next
    })

    try {
      await apiPostJson<any>('/api/favorites/toggle', { product_id: productId, favorite: !isFav })
    } catch (err) {
      console.error('Failed to toggle favorite:', err)
      setFavoriteProductIds((prev) => {
        const next = new Set(prev)
        if (isFav) next.add(productId)
        else next.delete(productId)
        return next
      })
    }
  }

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  useEffect(() => {
    if (!productsForToc.length) {
      setActiveProductId(null)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0))

        const top = visible[0]
        const id = top?.target?.getAttribute('data-product-id')
        if (id) setActiveProductId(id)
      },
      {
        root: null,
        rootMargin: '-10% 0px -70% 0px',
        threshold: [0.05, 0.1, 0.2, 0.3, 0.5]
      }
    )

    for (const p of productsForToc) {
      const el = productElsRef.current[p.id]
      if (el) observer.observe(el)
    }

    return () => observer.disconnect()
  }, [productsForToc])

  const scrollToProduct = useCallback((productId: string, highlightMode: 'ring' | 'blinkGreen' | 'none' = 'ring') => {
    const el = document.getElementById(`product-${productId}`)
    if (!el) return

    for (const t of highlightTimersRef.current) window.clearTimeout(t)
    highlightTimersRef.current = []

    const ringAmberClasses = ['ring-2', 'ring-amber-500']
    const blinkGreenClasses = ['ring-2', 'ring-emerald-500', 'bg-emerald-500/10']

    if (lastHighlightedElRef.current) {
      lastHighlightedElRef.current.classList.remove(...ringAmberClasses)
      lastHighlightedElRef.current.classList.remove(...blinkGreenClasses)
    }

    lastHighlightedElRef.current = el

    el.scrollIntoView({ behavior: 'smooth', block: 'start' })

    if (highlightMode === 'none') return

    if (highlightMode === 'ring') {
      el.classList.add(...ringAmberClasses)
      const t = window.setTimeout(() => {
        el.classList.remove(...ringAmberClasses)
      }, 1200)
      highlightTimersRef.current.push(t)
      return
    }

    const blinkOn = () => el.classList.add(...blinkGreenClasses)
    const blinkOff = () => el.classList.remove(...blinkGreenClasses)

    blinkOn()
    highlightTimersRef.current.push(window.setTimeout(blinkOff, 220))
    highlightTimersRef.current.push(window.setTimeout(blinkOn, 420))
    highlightTimersRef.current.push(window.setTimeout(blinkOff, 640))
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const sharedProductId = (params.get('product_id') || params.get('product') || '').trim()

    if (!sharedProductId) {
      handledSharedProductRef.current = null
      return
    }
    if (handledSharedProductRef.current === sharedProductId) {
      return
    }

    const product = products.find((p) => p.id === sharedProductId)
    if (!product) {
      return
    }

    handledSharedProductRef.current = sharedProductId

    if (product.category && filters.category !== product.category) {
      setFilters((prev) => prev.category === product.category ? prev : { ...prev, category: product.category })
    }

    const timer = window.setTimeout(() => {
      if (viewMode === 'compact') {
        setVariantsModalProduct(product)
        return
      }
      setActiveProductId(product.id)
      scrollToProduct(product.id, 'blinkGreen')
    }, 160)

    return () => window.clearTimeout(timer)
  }, [location.search, products, filters.category, viewMode, scrollToProduct])

  const selectProductFromPanel = useCallback((productId: string) => {
    const product = products.find((p) => p.id === productId)
    if (product) {
      setVariantsModalProduct(product)
    }
  }, [products])

  const openFavoritesFromPanel = useCallback(() => {
    setFilters((prev) => ({ ...prev, category: 'favorites' }))
    const params = new URLSearchParams(location.search)
    params.set('category', 'favorites')
    navigate({ pathname: '/products', search: `?${params.toString()}` }, { replace: true })
  }, [location.search, navigate])

  const openHotFromPanel = useCallback(() => {
    const firstHot = products.find((p) => !!p.is_hot && !p.is_paused)
    if (!firstHot) return
    selectProductFromPanel(firstHot.id)
  }, [products, selectProductFromPanel])

  useEffect(() => {
    const onOpenHot = () => openHotFromPanel()
    window.addEventListener('digigo-open-hot-product', onOpenHot)
    return () => window.removeEventListener('digigo-open-hot-product', onOpenHot)
  }, [openHotFromPanel])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const focus = String(params.get('focus') || '').trim().toLowerCase()
    if (focus !== 'hot') return

    openHotFromPanel()
    params.delete('focus')
    const search = params.toString()
    navigate({ pathname: '/products', search: search ? `?${search}` : '' }, { replace: true })
  }, [location.search, navigate, openHotFromPanel])

  const moveCategory = (categoryKey: string, direction: 'up' | 'down') => {
    if (!allowCategoryReorder) return
    if (categoryKey === 'favorites') return

    const base = categoryOrder.length ? categoryOrder : availableCategoryOrder
    const currentIndex = base.indexOf(categoryKey)
    if (currentIndex === -1) return
    if (direction === 'up' && currentIndex === 0) return
    if (direction === 'down' && currentIndex === base.length - 1) return
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    const next = [...base]
    const temp = next[targetIndex]
    next[targetIndex] = next[currentIndex]
    next[currentIndex] = temp
    setCategoryOrder(next)
    void persistCategoryOrder(next)
  }

  const fetchProducts = async () => {
    try {
      const resp = await apiGetJson<{ data: Product[] | null; error?: any }>('/api/products')
      const data = (resp?.data || []) as Product[]
      const normalized = data.map((product) => {
        const resolvedName = resolveProductName(product)
        const rawName = normalizeText(product.name)
        if (!rawName) {
          console.warn('[ProductsPage] Product thiếu tên, đã fallback:', {
            id: product.id,
            product_code: product.product_code,
            fallback_name: resolvedName
          })
        }
        return {
          ...product,
          name: resolvedName
        }
      })

      const sorted = [...normalized].sort((a, b) => {
        const orderA = a.sort_order ?? 999999
        const orderB = b.sort_order ?? 999999

        if (orderA !== orderB) {
          return orderA - orderB
        }
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })
      setProducts(sorted)
      setFilteredProducts(sorted)

    } catch (error) {
      console.error('Error fetching products:', error)
      setProducts([])
      setFilteredProducts([])
    } finally {
      setLoading(false)
    }
  }

  const resetFilters = () => {
    setFilters({
      category: 'all',
      search: '',
    })
    handleSearchInputChange('')
  }

  const handlePurchaseVariant = (variant: ProductVariant, productName: string) => {
    if (!user) {
      setShowAuthModal(true)
      return
    }

    const effectiveStock = ((variant as any).is_infinity || variant.stock === null)
      ? Number.POSITIVE_INFINITY
      : (variant.stock || 0)

    if (!variant.is_manual_delivery && effectiveStock <= 0) {
      alert('Gói này đã hết hàng!')
      return
    }

    if ((variant as any).is_archived) {
      alert('Gói này hiện đang bị ẩn')
      return
    }

    if ((variant as any).is_paused) {
      alert('Gói đang tạm dừng bán')
      return
    }

    // Find the product to get guide_url
    const product = products.find(p => p.variants?.some(v => v.id === variant.id))

    if (product?.is_paused) {
      alert('Sản phẩm đang tạm dừng bán')
      return
    }

    setSelectedVariant(variant)
    setSelectedProductName(productName)
    setSelectedProductGuideUrl(product?.guide_url)
    setQuantity(1)
    setSelectedComboId('')
    setSelectedComboQuantity(0)
    setVariantCombos([])
    setPurchaseResult(null)
    setShowConfirmModal(true)
  }

  useEffect(() => {
    if (!showConfirmModal) return
    if (!selectedVariant?.id) {
      setVariantCombos([])
      return
    }

    const loadCombos = async () => {
      setCombosLoading(true)
      try {
        let rows: any[] = []

        const resp = await apiGetJson<{ data: any[] | null }>(
          `/api/variant-combos?base_variant_id=${encodeURIComponent(selectedVariant.id)}`
        )
        rows = Array.isArray(resp?.data) ? resp.data : []

        const normalized = rows.map((c) => ({
          id: String(c.id),
          base_variant_id: String(c.base_variant_id),
          combo_name: String(c.combo_name || ''),
          required_points: Number(c.required_points || 1),
          is_active: !!c.is_active,
          sort_order: Number(c.sort_order || 0),
          items: Array.isArray(c.items) ? c.items : []
        })) as VariantCombo[]
        setVariantCombos(normalized)
      } catch (e) {
        console.error('Failed to load variant combos:', e)
        setVariantCombos([])
      } finally {
        setCombosLoading(false)
      }
    }

    void loadCombos()
  }, [showConfirmModal, selectedVariant?.id])

  const points = useMemo(() => {
    const factor = Math.max(0, Number((selectedVariant as any)?.point_factor ?? 1) || 1)
    return Math.max(0, quantity) * factor
  }, [quantity, selectedVariant])

  const selectedCombo = useMemo(() => {
    if (!selectedComboId) return null
    return (variantCombos || []).find((c) => c.id === selectedComboId) || null
  }, [selectedComboId, variantCombos])

  const maxComboQuantity = useMemo(() => {
    if (!selectedCombo) return 0
    const req = Math.max(1, Number(selectedCombo.required_points || 1))
    return Math.floor(points / req)
  }, [selectedCombo, points])

  useEffect(() => {
    if (!selectedComboId) {
      if (selectedComboQuantity !== 0) setSelectedComboQuantity(0)
      return
    }
    const next = Math.min(selectedComboQuantity, maxComboQuantity)
    if (next !== selectedComboQuantity) setSelectedComboQuantity(next)
    if (maxComboQuantity <= 0 && selectedComboQuantity !== 0) setSelectedComboQuantity(0)
  }, [maxComboQuantity, selectedComboId, selectedComboQuantity])

  const getUnitPrice = () => {
    if (!selectedVariant) return 0
    return calcUnitPrice(user, selectedVariant)
  }

  const getTotalPrice = () => {
    return getUnitPrice() * quantity
  }

  const canAfford = () => {
    return user && user.balance >= getTotalPrice()
  }

  const getMaxQuantity = () => {
    if (!selectedVariant) return 1

    if ((selectedVariant as any).is_infinity || selectedVariant.stock === null) {
      return 10
    }

    const effectiveStock = selectedVariant.stock || 0
    return Math.min(effectiveStock || 1, 10)
  }

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedKey(id)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  const copyProductShareLink = async (productId: string) => {
    try {
      const shareLink = buildProductShareLink(productId, user?.id || null)
      if (!shareLink) return
      await navigator.clipboard.writeText(shareLink)
      setCopiedShareProductId(productId)
      window.setTimeout(() => setCopiedShareProductId((prev) => prev === productId ? null : prev), 1800)
    } catch (error) {
      console.error('Không thể copy link giới thiệu sản phẩm:', error)
    }
  }

  const confirmPurchase = async () => {
    if (!selectedVariant || !user) return

    if (!canAfford()) {
      alert('Số dư không đủ. Vui lòng nạp tiền!')
      return
    }

    // Check if combos are available but user didn't select any
    const hasAvailableCombos = variantCombos.length > 0 && points >= Math.min(...variantCombos.map(c => c.required_points || 1))
    const shouldApplyCombo = !!selectedComboId && selectedComboQuantity > 0

    if (hasAvailableCombos && !shouldApplyCombo) {
      const confirmed = window.confirm('Bạn có quà tặng khả dụng nhưng chưa chọn. Bạn chắc chắn muốn mua mà KHÔNG nhận quà tặng không?')
      if (!confirmed) return
    }

    setPurchasing(true)
    try {
      const resp: any = await apiPostJson<any>('/api/purchase', {
        variant_id: selectedVariant.id,
        quantity,
        combo_id: shouldApplyCombo ? selectedComboId : undefined,
        combo_quantity: shouldApplyCombo ? selectedComboQuantity : undefined,
      })

      const purchaseOk = resp?.success === true || resp?.ok === true || resp?.data?.success === true
      const tx = unwrapFirst<any>(resp) || null
      const rpcData: any = resp?.data && typeof resp.data === 'object' && !Array.isArray(resp.data) ? resp.data : null
      const info: any = rpcData || tx || {}

      if (purchaseOk || !!(tx && tx.id)) {
        // Send Telegram notifications
        const username = user.username || 'N/A'
        const fullName = user.full_name || user.email || 'N/A'

        const dataAny = info as any

        const comboApplied = !!dataAny?.combo_applied
        const comboName = comboApplied ? String(dataAny?.combo_name || '') : ''
        const comboQty = comboApplied ? Number(dataAny?.combo_quantity || 0) : 0
        const giftKeyValues: string[] = Array.isArray(dataAny?.gift_key_values) ? dataAny.gift_key_values : []
        const giftOrderCodes: string[] = Array.isArray(dataAny?.gift_order_codes) ? dataAny.gift_order_codes : []
        const giftCount = giftKeyValues.length || giftOrderCodes.length

        // Lấy mã đơn hàng
        let orderCode = dataAny?.order_codes?.[0] as string | undefined

        // Fallback 1: Nếu RPC có trả transaction_id / transaction_ids
        if (!orderCode && typeof dataAny?.transaction_id === 'string') {
          orderCode = dataAny.transaction_id.split('-')[0].toUpperCase()
        }
        if (!orderCode && Array.isArray(dataAny?.transaction_ids) && dataAny.transaction_ids?.[0]) {
          orderCode = String(dataAny.transaction_ids[0]).split('-')[0].toUpperCase()
        }

        // Fallback 1.5: API chuẩn trả Transaction.id
        if (!orderCode && typeof (tx as any)?.id === 'string') {
          orderCode = String((tx as any).id)
        }

        // Fallback 2: manual delivery thường embed mã đơn trong key_values/keys
        if (!orderCode) {
          const kvs = dataAny?.key_values ?? dataAny?.keys ?? [dataAny?.key_value]
          const kv = kvs?.[0]
          if (typeof kv === 'string') {
            const m = kv.match(/Mã đơn.*:\s*([A-Za-z0-9]+)/)
            if (m) orderCode = m[1]
          }
        }

        // Fallback 3: Query transaction mới nhất (đảm bảo không ra N/A)
        if (!orderCode) {
          try {
            const txResp = await apiGetJson<any>(
              `/api/transactions/latest-purchase?variant_id=${encodeURIComponent(selectedVariant.id)}`
            )
            const txId = unwrapFirst<{ id?: string | null }>(txResp)?.id
            if (txId) orderCode = String(txId)
          } catch {
            // ignore
          }
        }

        if (!orderCode) orderCode = 'UNKNOWN'

        const orderCodeLine = `- Mã đơn hàng: <b>${orderCode}</b>`

        const comboLine = comboApplied
          ? `\n- Combo: <b>${comboName}</b> × <b>${comboQty}</b>\n- Quà tặng: <b>${giftCount}</b> mục`
          : ''

        // 1) Đơn hàng mới! (giữ thông tin giá)
        const unitPrice = dataAny?.final_unit_price ?? getUnitPrice()
        const unitPriceNumber = Number(unitPrice)
        const totalPriceFromRpc = Number(dataAny?.total_price)
        const totalPriceNumber = Number.isFinite(totalPriceFromRpc)
          ? totalPriceFromRpc
          : (Number.isFinite(unitPriceNumber) ? (unitPriceNumber * quantity) : getTotalPrice())
        const variantDisplay = selectedVariant.short_name ? `${selectedVariant.name} (${selectedVariant.short_name})` : selectedVariant.name
        const importVariantDisplay = selectedVariant.short_name || selectedVariant.name
        const isInfinityStock = (selectedVariant as any).is_infinity || selectedVariant.stock === null
        const selectedEffectiveStock = isInfinityStock
          ? Number.POSITIVE_INFINITY
          : (selectedVariant.stock || 0)
        const remainingStock = isInfinityStock
          ? '∞'
          : (typeof dataAny?.remaining_stock === 'number'
            ? dataAny.remaining_stock
            : Math.max(0, selectedEffectiveStock - quantity))

        const orderMsg = `<b>🛒 Đơn hàng mới!</b>\n\n- Họ tên: ${fullName}\n- Username: ${username}\n- Sản phẩm: ${selectedProductName}\n- Gói: ${variantDisplay}\n- Đơn giá: ${Number(unitPriceNumber).toLocaleString('vi-VN')}đ\n- Số lượng: ${quantity}\n- Số lượng hàng còn lại: ${remainingStock}\n- Tổng tiền: ${Number(totalPriceNumber).toLocaleString('vi-VN')}đ\n${orderCodeLine}${comboLine}`
        sendTelegramNotification(orderMsg, { username, full_name: fullName })

        // 2) Hãy nhập hàng! (KHÔNG có giá)
        const productOfVariant = products.find(p => p.variants?.some(v => v.id === selectedVariant.id))
        const supplierId = selectedVariant.supplier_id ?? null
        let supplierName = 'Chưa cập nhật'
        if (supplierId) {
          try {
            const sResp = await apiGetJson<any>(
              `/api/suppliers/name?id=${encodeURIComponent(String(supplierId))}`
            )
            const nm = unwrapFirst<{ full_name?: string | null }>(sResp)?.full_name
            if (nm) supplierName = String(nm)
          } catch {
            // ignore
          }
        }
        const safeSupplierName = supplierName
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')

        const safeProductCode = (productOfVariant?.product_code || 'Chưa cập nhật')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
        const safeProductName = (productOfVariant?.name || selectedProductName || 'Chưa cập nhật')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')

        const safeVariantName = (selectedVariant.name || 'Chưa cập nhật')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')

        const safeVariantPackage = (importVariantDisplay || 'Chưa cập nhật')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')

        const importComboLine = comboApplied
          ? `\n- Combo: ${comboName} × ${comboQty}\n- Quà tặng: ${giftCount} mục`
          : ''
        const importMsg = `<b>📦 Hãy nhập hàng - ${safeSupplierName}!</b>\n\n- Họ tên: ${fullName}\n- Username: ${username}\n- Số hiệu sản phẩm: ${safeProductCode}\n- Tên sản phẩm: ${safeProductName}\n- Tên gói sản phẩm: ${safeVariantName}\n- Gói: ${safeVariantPackage}\n- Số lượng: ${quantity}\n${orderCodeLine}${importComboLine}`
        sendTelegramNotification(importMsg, { username, full_name: fullName })

        const purchaseToastTitle = selectedVariant.is_manual_delivery ? 'Đặt đơn thành công' : 'Mua hàng thành công'
        const purchaseToastMessage = selectedVariant.is_manual_delivery
          ? `${selectedProductName} • ${selectedVariant.name} • SL ${quantity}. Support sẽ xử lý đơn thủ công.`
          : `${selectedProductName} • ${selectedVariant.name} • SL ${quantity} • ${Number(totalPriceNumber).toLocaleString('vi-VN')}đ.`

        toast({
          variant: 'success',
          title: purchaseToastTitle,
          message: purchaseToastMessage,
          durationMs: 5000,
          saveToNotifications: true,
        })

        // Cảnh báo hết hàng chỉ áp dụng cho auto-delivery (có kho key)
        if (!selectedVariant.is_manual_delivery && (selectedVariant.stock || 0) <= quantity) {
          const oosMsg = `<b>🚨 CẢNH BÁO HẾT HÀNG!</b>\n\n- Sản phẩm: ${selectedProductName}\n- Gói: ${selectedVariant.name}\n- Kho đã hết key.`
          sendTelegramNotification(oosMsg)
        }

        // Show purchase result
        const keyValues: string[] = (() => {
          const raw = (rpcData as any)?.key_values ?? (rpcData as any)?.keys
          if (Array.isArray(raw) && raw.length) return raw.map((x: any) => String(x))
          const one = (rpcData as any)?.key_value
          if (typeof one === 'string' && one.trim()) return [one]
          const txOne = (tx as any)?.key_value
          if (typeof txOne === 'string' && txOne.trim()) return [txOne]
          return []
        })()

        const guideUrlResolved =
          (rpcData as any)?.guide_url || (tx as any)?.guide_url || selectedVariant.guide_url || selectedProductGuideUrl

        setPurchaseResult({
          keys: keyValues.length ? keyValues : [''],
          giftKeys: giftKeyValues,
          guideUrl: guideUrlResolved,
          // Store these for copy logic
          productName: String((tx as any)?.product_name || selectedProductName || ''),
          variantName: String((tx as any)?.variant_name || selectedVariant.name || ''),
          comboName: comboApplied ? comboName : undefined,
          comboQuantity: comboApplied ? comboQty : undefined,
          manualDelivery: selectedVariant.is_manual_delivery
        })

        await refreshProfile()
        await fetchProducts()
      }
    } catch (error: any) {
      console.error('Error purchasing:', error)
      alert(`Lỗi: ${error.message || 'Có lỗi xảy ra khi mua hàng'}`)
    } finally {
      setPurchasing(false)
    }
  }

  const closeModal = () => {
    setShowConfirmModal(false)
    setSelectedVariant(null)
    setPurchaseResult(null)
    setQuantity(1)
    setSelectedComboId('')
    setSelectedComboQuantity(0)
    setVariantCombos([])
  }

  const truncate100 = (value?: string | null) => {
    const text = (value || '').trim()
    if (!text) return '-'
    if (text.length <= 100) return text
    return `${text.slice(0, 100)}...`
  }

  const getMaxSalePercent = (p: Product) => {
    const variants = (p.variants || []) as ProductVariant[]
    if (!variants.length) return 0
    let max = 0
    for (const v of variants) {
      const { totalPercentAppliedToPrice } = computePercent(user, v)
      if (totalPercentAppliedToPrice > max) max = totalPercentAppliedToPrice
    }
    return max
  }

  const showFullTextPreview = (text: string, ev: React.MouseEvent) => {
    if (!text?.trim()) return
    setHoverPreview({ text, x: ev.clientX, y: ev.clientY })
  }

  const moveFullTextPreview = (ev: React.MouseEvent) => {
    setHoverPreview(prev => (prev ? { ...prev, x: ev.clientX, y: ev.clientY } : prev))
  }

  const hideFullTextPreview = () => setHoverPreview(null)

  const handleSupportChat = () => {
    // Logic to open support chat
    // Assuming there's a global chat mechanism or redirect
    // For now, we'll try to trigger the chat widget if available, or redirect to chat page
    // Since we're in a modal, maybe we can just close modal and open chat?
    // But the user might want to keep the modal open to copy keys.
    // Let's assume we can dispatch an event or use a global context.
    // For simplicity, we'll alert for now if no direct method, or try to find a chat trigger.

    // Dispatch a custom event that the layout or chat component listens to
    window.dispatchEvent(new CustomEvent('open-chat-support', {
      detail: {
        message: `Tôi vừa mua đơn hàng: ${purchaseResult?.keys.join(', ')} (${selectedProductName} - ${selectedVariant?.name}). Nhờ hỗ trợ kích hoạt/nhận key.`,
        autoReply: selectedVariant?.manual_delivery_auto_reply || undefined
      }
    }))

    closeModal()
  }

  useEffect(() => {
    const borderStyle = { borderColor: 'var(--customer-leftbar-border, rgba(15,23,42,0.12))' } as const

    setLeftSidebar({
      title: 'Mục lục sản phẩm',
      content: (
        <div>
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 opacity-70" />
            <input
              type="text"
              value={tocSearch}
              onChange={(e) => setTocSearch(e.target.value)}
              placeholder="Tìm trong mục lục..."
              className="w-full pl-9 pr-3 py-2 rounded-xl border bg-transparent text-[13px] outline-none transition-colors"
              style={borderStyle}
            />
          </div>

          <div className="max-h-[60vh] overflow-auto pr-1">
            <div className="space-y-1">
              {tocCategoryKeys.map((categoryKey) => {
                const items = categoryKey === 'favorites'
                  ? filteredProductsForToc.filter((p) => favoriteProductIds.has(p.id))
                  : filteredProductsForToc.filter((p) => p.category === categoryKey)
                const isOver = tocOverCategoryReorder === categoryKey

                return (
                  <div
                    key={categoryKey}
                    onDragOver={(e) => {
                      if (!allowCategoryReorder) return
                      if (categoryKey === 'favorites') return
                      e.preventDefault()
                      if (!tocDraggingCategory) return
                      setTocOverCategoryReorder(categoryKey)
                      e.dataTransfer.dropEffect = 'move'
                    }}
                    onDragEnter={() => {
                      if (!allowCategoryReorder) return
                      if (categoryKey === 'favorites') return
                      if (!tocDraggingCategory) return
                      setTocOverCategoryReorder(categoryKey)
                    }}
                    onDragLeave={() => {
                      if (tocOverCategoryReorder === categoryKey) setTocOverCategoryReorder(null)
                    }}
                    onDrop={(e) => {
                      if (!allowCategoryReorder) return
                      if (categoryKey === 'favorites') {
                        setTocDraggingCategory(null)
                        setTocOverCategoryReorder(null)
                        return
                      }
                      e.preventDefault()
                      const raw = e.dataTransfer.getData('text/plain')
                      const dragKey = (tocDraggingCategory || raw || '').replace(/^cat:/, '')
                      if (!dragKey) {
                        setTocDraggingCategory(null)
                        setTocOverCategoryReorder(null)
                        return
                      }
                      const base = categoryOrder.length ? categoryOrder : availableCategoryOrder
                      const from = base.indexOf(dragKey)
                      const to = base.indexOf(categoryKey)
                      if (from === -1 || to === -1 || from === to) {
                        setTocDraggingCategory(null)
                        setTocOverCategoryReorder(null)
                        return
                      }
                      const next = [...base]
                      next.splice(from, 1)
                      next.splice(to, 0, dragKey)
                      setCategoryOrder(next)
                      void persistCategoryOrder(next)
                      setTocDraggingCategory(null)
                      setTocOverCategoryReorder(null)
                    }}
                    className={
                      `rounded-2xl border ` + (isOver ? 'ring-2 ring-amber-300' : '')
                    }
                    style={borderStyle}
                  >
                    <div
                      draggable={allowCategoryReorder && categoryKey !== 'favorites'}
                      onDragStart={(e) => {
                        if (!allowCategoryReorder) return
                        if (categoryKey === 'favorites') return
                        setTocDraggingCategory(categoryKey)
                        e.dataTransfer.setData('text/plain', `cat:${categoryKey}`)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => {
                        setTocDraggingCategory(null)
                        setTocOverCategoryReorder(null)
                      }}
                      className="px-3 py-2 flex items-center justify-between gap-2"
                    >
                      <div className="text-[12px] font-bold tracking-wide uppercase opacity-85">
                        {categoryKey === 'favorites' ? 'Yêu thích' : getCategoryLabel(categoryKey)}
                      </div>
                      <div className="text-[11px] opacity-75">
                        {items.length}
                      </div>
                    </div>

                    <div className="px-2 pb-2 space-y-1">
                      {items.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => scrollToProduct(p.id)}
                          className={
                            'w-full text-left px-2 py-1.5 rounded-xl text-[13px] transition-colors '
                            + (activeProductId === p.id ? 'bg-amber-500/20' : 'hover:bg-amber-500/10')
                          }
                          title={p.name}
                        >
                          <div className="line-clamp-2">{p.name}</div>
                        </button>
                      ))}
                      {items.length === 0 ? (
                        <div className="px-2 py-2 text-[13px] opacity-70">
                          {tocSearch.trim() ? 'Không tìm thấy sản phẩm' : 'Không có sản phẩm'}
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      ),
    })

    return () => clearLeftSidebar()
  }, [
    activeProductId,
    allowCategoryReorder,
    availableCategoryOrder,
    categoryOrder,
    clearLeftSidebar,
    favoriteProductIds,
    filteredProductsForToc,
    getCategoryLabel,
    persistCategoryOrder,
    scrollToProduct,
    setLeftSidebar,
    tocCategoryKeys,
    tocDraggingCategory,
    tocOverCategoryReorder,
    tocSearch,
  ])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    )
  }

  return (
    <div className="products-page w-full">

      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Sản Phẩm</h1>
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1">
            <button
              type="button"
              onClick={() => {
                setViewMode('full')
                localStorage.setItem('taphoanoidungso-products-view', 'full')
              }}
              className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${viewMode === 'full'
                ? 'bg-amber-600 text-white'
                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
            >
              Đầy đủ
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode('compact')
                localStorage.setItem('taphoanoidungso-products-view', 'compact')
              }}
              className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${viewMode === 'compact'
                ? 'bg-amber-600 text-white'
                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
            >
              Thu gọn
            </button>
          </div>
        </div>
        <div className="mb-4 sm:mb-6 text-sm text-gray-600 dark:text-gray-100">
          Cần tư vấn hoặc hỗ trợ nhanh? Nhấn vào đây:&nbsp;
          <a
            href="https://t.me/+_KxbPXTAu7AzYjg1"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-600 dark:text-amber-300 hover:underline font-medium"
          >
            Telegram
          </a>
          &nbsp;|&nbsp;
          <a
            href="https://zalo.me/0794174833"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-600 dark:text-amber-300 hover:underline font-medium"
          >
            Zalo
          </a>
        </div>

        <div className="mb-6 hidden md:block">
          <ProductsUnifiedPanel
            products={products}
            favoriteProductIds={favoriteProductIds}
            favoritesLoading={favoritesLoading}
            onToggleFavorite={toggleFavorite}
            onSelectProduct={selectProductFromPanel}
            onOpenFavorites={openFavoritesFromPanel}
            onOpenHot={openHotFromPanel}
            shareReferrerId={user?.id || null}
          />
        </div>

        {/* Filters */}
        {/* Search & Filter Section - New Layout */}
        <div className="mb-8 space-y-4">
          {/* Main Search Bar - Prominent */}
          <div className="relative group">
            {/* Gradient border effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 via-orange-400 to-amber-500 rounded-2xl blur opacity-30 group-hover:opacity-50 group-focus-within:opacity-60 transition-opacity duration-300" />
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl p-1.5 shadow-lg">
              <div className="relative flex items-center">
                <div className="absolute left-4 pointer-events-none">
                  <Search className="h-5 w-5 sm:h-6 sm:w-6 text-amber-500" />
                </div>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  placeholder="🔍 Tìm kiếm sản phẩm, gói dịch vụ..."
                  className="w-full pl-12 sm:pl-14 pr-12 py-4 sm:py-5 bg-gray-50/50 dark:bg-gray-800/50 border-0 rounded-xl text-base sm:text-lg text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-0 focus:outline-none transition-all"
                />
                {searchInput && (
                  <button
                    onClick={() => handleSearchInputChange('')}
                    className="absolute right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Category Pills & Reset */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Category Dropdown styled as pill */}
            <div className="relative">
              <select
                value={filters.category}
                onChange={(e) => {
                  const cat = e.target.value
                  setFilters({ ...filters, category: cat })

                  const params = new URLSearchParams(location.search)
                  if (cat === 'all') {
                    params.delete('category')
                  } else {
                    params.set('category', cat)
                  }
                  const search = params.toString()
                  navigate({ pathname: '/products', search: search ? `?${search}` : '' }, { replace: true })
                }}
                className="appearance-none pl-4 pr-10 py-2.5 sm:py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100 cursor-pointer hover:border-amber-400 hover:shadow-md transition-all focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              >
                <option value="all">✨ Tất cả danh mục</option>
                <option value="favorites">❤️ Yêu thích</option>
                {(categories || []).length ? (
                  (categories || [])
                    .map((c) => c.key)
                    .filter(Boolean)
                    .filter((key, idx, arr) => arr.indexOf(key) === idx)
                    .map((key) => (
                      <option key={key} value={key}>{getCategoryLabel(key)}</option>
                    ))
                ) : (
                  <>
                    <option value="software">Phần mềm</option>
                    <option value="game">Game</option>
                    <option value="education">Giáo dục</option>
                    <option value="programming">Lập trình</option>
                    <option value="social">Mạng xã hội</option>
                    <option value="creation">Sáng tạo nội dung</option>
                  </>
                )}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              </div>
            </div>

            {/* Quick Category Pills */}
            <div className="hidden sm:flex flex-wrap gap-2">
              {(categories || []).slice(0, 4).map((c) => (
                <button
                  key={c.key}
                  onClick={() => {
                    setFilters({ ...filters, category: c.key })
                    const params = new URLSearchParams(location.search)
                    params.set('category', c.key)
                    navigate({ pathname: '/products', search: `?${params.toString()}` }, { replace: true })
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${filters.category === c.key
                    ? 'bg-amber-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:text-amber-600 dark:hover:text-amber-400'
                    }`}
                >
                  {c.label || c.key}
                </button>
              ))}
            </div>

            {/* Spacer */}
            <div className="flex-grow" />

            {/* Reset Button */}
            <button
              onClick={resetFilters}
              className="flex items-center gap-2 px-4 py-2.5 sm:py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-full transition-all hover:shadow-md text-sm sm:text-base font-medium"
            >
              <RotateCcw className="h-4 w-4" />
              <span className="hidden sm:inline">Đặt lại</span>
            </button>
          </div>
        </div>

        {isTocOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/30"
            onClick={() => setIsTocOpen(false)}
            aria-hidden="true"
          />
        )}

        <aside className="lg:hidden fixed left-0 right-0 bottom-0 z-50">
          <div
            className={
              "mx-auto w-full sm:max-w-2xl " +
              "bg-white dark:bg-gray-900 shadow-lg border border-gray-200 dark:border-gray-800 rounded-t-xl p-3 " +
              "transition-transform duration-200 ease-out " +
              (isTocOpen ? "translate-y-0" : "translate-y-[calc(100%-3.5rem)]")
            }
          >
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setIsTocOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm font-semibold transition-colors shadow-sm"
                aria-expanded={isTocOpen}
              >
                <Package className="h-4 w-4" />
                {isTocOpen ? 'Thu gọn' : 'Mở rộng'}
              </button>
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                Mục lục sản phẩm
              </div>
            </div>

            <div className={isTocOpen ? 'mt-3' : 'hidden'}>
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-300" />
                <input
                  type="text"
                  value={tocSearch}
                  onChange={(e) => setTocSearch(e.target.value)}
                  placeholder="Tìm trong mục lục..."
                  className="rainbow-control w-full pl-9 pr-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md focus:ring-amber-500 focus:border-amber-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-300"
                />
              </div>

              <div className="max-h-[55vh] overflow-auto pr-1">
                <div className="space-y-1">
                  {tocCategoryKeys.map((categoryKey) => {
                    const items = categoryKey === 'favorites'
                      ? filteredProductsForToc.filter((p) => favoriteProductIds.has(p.id))
                      : filteredProductsForToc.filter((p) => p.category === categoryKey)
                    const isOver = tocOverCategoryReorder === categoryKey

                    return (
                      <div
                        key={categoryKey}
                        onDragOver={(e) => {
                          if (!allowCategoryReorder) return
                          if (categoryKey === 'favorites') return
                          e.preventDefault()
                          if (!tocDraggingCategory) return
                          setTocOverCategoryReorder(categoryKey)
                          e.dataTransfer.dropEffect = 'move'
                        }}
                        onDragEnter={() => {
                          if (!allowCategoryReorder) return
                          if (categoryKey === 'favorites') return
                          if (!tocDraggingCategory) return
                          setTocOverCategoryReorder(categoryKey)
                        }}
                        onDragLeave={() => {
                          if (tocOverCategoryReorder === categoryKey) setTocOverCategoryReorder(null)
                        }}
                        onDrop={(e) => {
                          if (!allowCategoryReorder) return
                          if (categoryKey === 'favorites') {
                            setTocDraggingCategory(null)
                            setTocOverCategoryReorder(null)
                            return
                          }
                          e.preventDefault()
                          const raw = e.dataTransfer.getData('text/plain')
                          const dragKey = (tocDraggingCategory || raw || '').replace(/^cat:/, '')
                          if (!dragKey) {
                            setTocDraggingCategory(null)
                            setTocOverCategoryReorder(null)
                            return
                          }
                          const base = categoryOrder.length ? categoryOrder : availableCategoryOrder
                          const from = base.indexOf(dragKey)
                          const to = base.indexOf(categoryKey)
                          if (from === -1 || to === -1 || from === to) {
                            setTocDraggingCategory(null)
                            setTocOverCategoryReorder(null)
                            return
                          }
                          const next = [...base]
                          next.splice(from, 1)
                          next.splice(to, 0, dragKey)
                          setCategoryOrder(next)
                          void persistCategoryOrder(next)
                          setTocDraggingCategory(null)
                          setTocOverCategoryReorder(null)
                        }}
                        className={
                          `rounded-lg border border-gray-200 dark:border-gray-800 ` +
                          (isOver ? 'ring-2 ring-amber-300' : '')
                        }
                      >
                        <div
                          draggable={allowCategoryReorder && categoryKey !== 'favorites'}
                          onDragStart={(e) => {
                            if (!allowCategoryReorder) return
                            if (categoryKey === 'favorites') return
                            const payload = `cat:${categoryKey}`
                            setTocDraggingCategory(categoryKey)
                            e.dataTransfer.setData('text/plain', payload)
                            e.dataTransfer.effectAllowed = 'move'
                          }}
                          onDragEnd={() => {
                            setTocDraggingCategory(null)
                            setTocOverCategoryReorder(null)
                          }}
                          className="px-2 py-2 bg-gray-50 dark:bg-gray-950/30 border-b border-gray-200 dark:border-gray-800"
                          title={categoryKey === 'favorites' ? 'Danh mục cố định' : 'Danh mục'}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wide truncate">
                                {categoryKey === 'favorites' ? 'Yêu thích' : getCategoryLabel(categoryKey)}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {allowCategoryReorder && categoryKey !== 'favorites' && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => moveCategory(categoryKey, 'up')}
                                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1 bg-white/70 dark:bg-gray-900/40 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                                    title="Lên"
                                  >
                                    <span className="sr-only">Lên</span>
                                    <span className="block w-3 h-3 border-t-2 border-l-2 border-current rotate-45 mt-1"></span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveCategory(categoryKey, 'down')}
                                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1 bg-white/70 dark:bg-gray-900/40 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                                    title="Xuống"
                                  >
                                    <span className="sr-only">Xuống</span>
                                    <span className="block w-3 h-3 border-b-2 border-r-2 border-current rotate-45 mb-1"></span>
                                  </button>
                                </>
                              )}
                              <div className="text-[11px] text-gray-500 dark:text-gray-400 px-1">
                                {items.length}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="p-1 space-y-1">
                          {items.map((p) => {
                            const isActive = activeProductId === p.id
                            return (
                              <div
                                key={p.id}
                                className={
                                  `group flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer select-none ` +
                                  (isActive
                                    ? 'bg-amber-50 text-amber-700 dark:bg-blue-950/30 dark:text-blue-200'
                                    : 'hover:bg-gray-50 text-gray-700 dark:text-gray-200 dark:hover:bg-gray-800')
                                }
                                title="Click để cuộn nhanh"
                                onClick={() => scrollToProduct(p.id)}
                              >
                                <div className="flex flex-col items-center justify-center text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300">
                                  <span className="w-3 h-0.5 bg-current mb-0.5 rounded"></span>
                                  <span className="w-3 h-0.5 bg-current mb-0.5 rounded"></span>
                                  <span className="w-3 h-0.5 bg-current rounded"></span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-sm font-medium">{p.name}</div>
                                  <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">{getCategoryLabel(p.category)}</div>
                                </div>
                              </div>
                            )
                          })}
                          {items.length === 0 && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 py-2 px-2">Không có sản phẩm</div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                  {filteredProductsForToc.length === 0 && (
                    <div className="text-sm text-gray-400 dark:text-gray-500 py-2">
                      {tocSearch.trim() ? 'Không tìm thấy sản phẩm' : 'Không có sản phẩm'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </aside>

        {viewMode === 'full' ? (
          <div className="space-y-5 sm:space-y-7">
            {filters.category === 'favorites' && (
              <section key="__favorites__" className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">
                    Yêu thích
                  </h2>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {favoritesLoading ? '...' : filteredProducts.length}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        id={`product-${product.id}`}
                        data-product-id={product.id}
                        onPointerEnter={() => setActiveProductId(product.id)}
                        ref={(el) => {
                          productElsRef.current[product.id] = el
                        }}
                        className="transition-all product-card-shell"
                      >
                        <ProductCard
                          product={product}
                          user={user}
                          onPurchase={handlePurchaseVariant}
                          isFavorite={favoriteProductIds.has(product.id)}
                          onToggleFavorite={toggleFavorite}
                          onSelectPackage={() => setVariantsModalProduct(product)}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-sm text-gray-500 dark:text-gray-400 italic">
                      Chưa có sản phẩm yêu thích.
                    </div>
                  )}
                </div>
              </section>
            )}
            {filters.category !== 'favorites' &&
              (allowCategoryReorder && categoryOrder.length ? categoryOrder : availableCategoryOrder)
                .filter((c) => filteredProducts.some((p) => p.category === c))
                .map((categoryKey) => {
                  const items = filteredProducts.filter((p) => p.category === categoryKey)
                  if (!items.length) return null

                  return (
                    <section key={categoryKey} className="space-y-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {getCategoryLabel(categoryKey)}
                        </h2>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {items.length}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                        {items.map((product) => (
                          <div
                            key={product.id}
                            id={`product-${product.id}`}
                            data-product-id={product.id}
                            onPointerEnter={() => setActiveProductId(product.id)}
                            ref={(el) => {
                              productElsRef.current[product.id] = el
                            }}
                            className="transition-all product-card-shell"
                          >
                            <ProductCard
                              product={product}
                              user={user}
                              onPurchase={handlePurchaseVariant}
                              isFavorite={favoriteProductIds.has(product.id)}
                              onToggleFavorite={toggleFavorite}
                              onSelectPackage={() => setVariantsModalProduct(product)}
                            />
                          </div>
                        ))}
                      </div>
                    </section>
                  )
                })}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr className="text-left">
                    <th className="px-3 py-3 text-xs font-bold text-slate-700 dark:text-slate-200 uppercase">STT</th>
                    <th className="px-3 py-3 text-xs font-bold text-cyan-700 dark:text-cyan-200 uppercase">Ảnh</th>
                    <th className="px-3 py-3 text-xs font-bold text-amber-700 dark:text-blue-200 uppercase">Tên dịch vụ</th>
                    <th className="px-3 py-3 text-xs font-bold text-indigo-700 dark:text-indigo-200 uppercase">Cơ chế</th>
                    <th className="px-3 py-3 text-xs font-bold text-emerald-700 dark:text-emerald-200 uppercase">Ưu điểm</th>
                    <th className="px-3 py-3 text-xs font-bold text-rose-700 dark:text-rose-200 uppercase">Nhược điểm</th>
                    <th className="px-3 py-3 text-xs font-bold text-amber-700 dark:text-amber-200 uppercase">Giảm giá</th>
                    <th className="px-3 py-3 text-xs font-bold text-violet-700 dark:text-violet-200 uppercase">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {(() => {
                    let runningIndex = 0
                    const groups = (allowCategoryReorder && categoryOrder.length ? categoryOrder : availableCategoryOrder)
                      .filter((c) => filteredProducts.some((p) => p.category === c))
                      .map((categoryKey) => ({
                        categoryKey,
                        items: filteredProducts.filter((p) => p.category === categoryKey)
                      }))

                    return groups.flatMap((g) => {
                      const head = (
                        <tr key={`cat-${g.categoryKey}`} className="bg-gray-50/80 dark:bg-gray-800/60">
                          <td className="px-3 py-2 font-bold text-gray-800 dark:text-gray-100" colSpan={8}>
                            <div className="flex items-center justify-between">
                              <span>{getCategoryLabel(g.categoryKey)}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-300">{g.items.length}</span>
                            </div>
                          </td>
                        </tr>
                      )

                      const rows = g.items.map((p) => {
                        runningIndex += 1
                        const salesPercent = getMaxSalePercent(p)

                        return (
                          <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                            <td className="px-3 py-3 align-top text-gray-700 dark:text-gray-200 font-semibold">{runningIndex}</td>
                            <td className="px-3 py-3 align-top">
                              <div className="w-20 aspect-[3/2] rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                <img
                                  src={p.image_url || 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=200'}
                                  alt={p.name}
                                  loading="lazy"
                                  decoding="async"
                                  width={80}
                                  height={53}
                                  className="w-full h-full object-fill"
                                />
                              </div>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div className="font-bold text-gray-900 dark:text-gray-100">
                                {truncate100(p.name)}
                              </div>
                              {p.recommended_model && (
                                <div className="text-xs text-amber-600 dark:text-amber-300 mt-1">
                                  {truncate100(p.recommended_model)}
                                </div>
                              )}
                            </td>
                            {(
                              [
                                { key: 'mechanism', value: p.mechanism },
                                { key: 'strengths', value: p.strengths },
                                { key: 'weaknesses', value: p.weaknesses },
                              ] as const
                            ).map((col) => (
                              <td
                                key={col.key}
                                className="px-3 py-3 align-top text-gray-700 dark:text-gray-200"
                                onMouseEnter={(ev) => showFullTextPreview(col.value || '', ev)}
                                onMouseMove={moveFullTextPreview}
                                onMouseLeave={hideFullTextPreview}
                              >
                                <div className="max-w-[320px] whitespace-pre-wrap break-words">
                                  {truncate100(col.value)}
                                </div>
                              </td>
                            ))}
                            <td className="px-3 py-3 align-top">
                              {salesPercent > 0 ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-200 border border-red-100 dark:border-red-900/40">
                                  {salesPercent}%
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">-</span>
                              )}
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div className="flex flex-col gap-2">
                                <button
                                  type="button"
                                  onClick={() => setVariantsModalProduct(p)}
                                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold"
                                >
                                  <Package className="h-4 w-4" />
                                  Xem các gói
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void copyProductShareLink(p.id)}
                                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-bold"
                                >
                                  {copiedShareProductId === p.id ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
                                  {copiedShareProductId === p.id ? 'Đã sao chép' : 'Tạo link giới thiệu'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })

                      return [head, ...rows]
                    })
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {hoverPreview && (
          <div
            className="fixed z-[60] pointer-events-none"
            style={{ left: Math.min(hoverPreview.x + 12, window.innerWidth - 380), top: Math.min(hoverPreview.y + 12, window.innerHeight - 200) }}
          >
            <div className="max-w-[360px] max-h-[180px] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-3 text-xs text-gray-900 dark:text-gray-100">
              {hoverPreview.text}
            </div>
          </div>
        )}

        {variantsModalProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-4 pb-[env(safe-area-inset-bottom,0.5rem)] overflow-y-auto z-50">
            <div className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-lg shadow-2xl my-auto h-[min(34rem,calc(100vh-2rem))] overflow-hidden border border-gray-200 dark:border-gray-800 flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-amber-600" />
                  <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                    Chọn gói ({variantsModalProduct.variants?.length || 0})
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setVariantsModalProduct(null)}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  aria-label="Đóng"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-4 space-y-3 overflow-y-auto flex-1 bg-gray-50 dark:bg-gray-950/30">
                {(variantsModalProduct.variants || []).map((variant) => {
                  const effectiveStock = ((variant as any)?.is_infinity || (variant as any)?.stock === null)
                    ? Number.POSITIVE_INFINITY
                    : ((variant as any)?.stock || 0)

                  const durationDays = (variant as any)?.duration_days ?? 0
                  const durationLabel = durationDays === 0 ? 'Trọn đời' : `${durationDays} ngày`
                  const warrantyLabel = (() => {
                    const mode = (variant as any)?.warranty_mode as ('none' | 'full' | 'partial' | undefined)
                    const days = (variant as any)?.warranty_days as (number | null | undefined)
                    const fallback = durationDays > 0 ? `${durationDays} ngày` : 'Trọn gói'
                    if (mode === 'none') return 'Không'
                    if (mode === 'full') return 'Trọn gói'
                    if (days == null) return fallback
                    if (days > 0) return `${days} ngày`
                    return 'Không'
                  })()

                  const isProductPaused = !!variantsModalProduct.is_paused
                  const isVariantPaused = !!(variant as any)?.is_paused
                  const isPaused = isProductPaused || isVariantPaused
                  const variantDisplayName = variant.name?.trim() || durationLabel

                  return (
                    <div
                      key={variant.id}
                      className="relative bg-white dark:bg-gray-900 p-4 border border-gray-200 dark:border-gray-800 rounded-lg hover:border-blue-400 transition-all"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-bold text-gray-900 dark:text-gray-100 text-sm break-words">
                            {variantDisplayName}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                            <div className="text-[11px] text-gray-500 dark:text-gray-400">
                              Thời lượng: {durationLabel}
                            </div>
                            <div className="text-[11px] text-green-600 dark:text-green-300 font-medium">
                              Bảo hành: {warrantyLabel}
                            </div>
                          </div>
                          {!!String((variant as any)?.description || '').trim() && (
                            <div className="text-xs text-gray-600 dark:text-gray-300 mt-2 whitespace-pre-wrap break-words">
                              {(variant as any).description}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <div className="text-right">
                            {calcUnitPrice(user, variant as ProductVariant) < (variant.price || 0) ? (
                              <>
                                <div className="text-xs text-gray-400 line-through">
                                  {(variant.price || 0).toLocaleString('vi-VN')}đ
                                </div>
                                <div className="flex items-center gap-2 justify-end">
                                  <div className="text-base font-extrabold text-red-600">
                                    {(calcUnitPrice(user, variant as ProductVariant) || 0).toLocaleString('vi-VN')}đ
                                  </div>
                                  {(() => {
                                    const { totalPercentAppliedToPrice } = computePercent(user, variant as ProductVariant)
                                    return totalPercentAppliedToPrice > 0 ? (
                                      <span className="text-[10px] bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-200 px-1.5 py-0.5 rounded border border-red-100 dark:border-red-900/40 font-semibold">
                                        -{totalPercentAppliedToPrice}%
                                      </span>
                                    ) : null
                                  })()}
                                </div>
                              </>
                            ) : (
                              <div className="text-base font-extrabold text-amber-600">
                                {(variant.price || 0).toLocaleString('vi-VN')}đ
                              </div>
                            )}

                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                              Còn lại: {(variant as any)?.is_manual_delivery ? '∞' : effectiveStock}
                            </div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400">
                              Đã bán: {variant.total_sold || 0}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setVariantsModalProduct(null)
                              handlePurchaseVariant(variant as ProductVariant, variantsModalProduct.name)
                            }}
                            disabled={effectiveStock <= 0 || isPaused}
                            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-[11px] sm:text-xs font-bold uppercase tracking-normal sm:tracking-wider transition-all shadow-sm flex items-center gap-1 ${effectiveStock <= 0 || isPaused
                              ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                              : 'bg-amber-600 text-white hover:bg-amber-700'
                              }`}
                          >
                            <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4" />
                            {isPaused ? 'Ngừng bán' : 'Mua ngay'}
                          </button>
                        </div>
                      </div>

                      {effectiveStock <= 0 && !isPaused ? (
                        <div className="absolute left-4 bottom-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900/40">
                            Hết hàng
                          </span>
                        </div>
                      ) : null}

                      {isPaused && (
                        <div className="absolute left-4 bottom-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900/40">
                            Ngừng bán
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })}

                {(!variantsModalProduct.variants || variantsModalProduct.variants.length === 0) && (
                  <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm italic">
                    Đang cập nhật gói sản phẩm...
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">
              {products.length === 0 ? 'Chưa có sản phẩm' : 'Không tìm thấy sản phẩm nào phù hợp'}
            </div>
          </div>
        )}

        {/* Purchase Confirmation Modal */}
        {showConfirmModal && selectedVariant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-4 pb-[env(safe-area-inset-bottom,0.5rem)] overflow-y-auto z-50">
            <div className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-lg shadow-2xl my-auto h-[min(42rem,calc(100vh-2rem))] overflow-hidden flex flex-col">
              <div className="p-6 overflow-y-auto flex-1">
                {!purchaseResult ? (
                  <>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5 text-amber-600" />
                      Xác nhận mua hàng
                    </h3>

                    {/* Product Info */}
                    <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 mb-4">
                      <p className="text-gray-600 dark:text-gray-200">
                        <span className="text-gray-500 dark:text-gray-400">Sản phẩm:</span>{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{selectedProductName}</span>
                      </p>
                      <p className="text-gray-600 dark:text-gray-200 mt-1">
                        <span className="text-gray-500 dark:text-gray-400">Gói:</span>{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{selectedVariant.name?.trim() || ((selectedVariant.duration_days || 0) === 0 ? 'Vĩnh viễn' : `${selectedVariant.duration_days} ngày`)}</span>
                      </p>
                      <p className="text-gray-600 dark:text-gray-200 mt-1">
                        <span className="text-gray-500 dark:text-gray-400">Thời hạn:</span>{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {(selectedVariant.duration_days || 0) === 0 ? 'Vĩnh viễn' : `${selectedVariant.duration_days} ngày`}
                        </span>
                      </p>
                      <p className="text-gray-600 dark:text-gray-200 mt-1">
                        <span className="text-gray-500 dark:text-gray-400">Còn lại:</span>{' '}
                        <span className="font-semibold text-yellow-700 dark:text-yellow-400">{((selectedVariant as any).is_infinity || selectedVariant.stock === null ? '∞' : (selectedVariant.stock || 0))} sản phẩm</span>
                      </p>
                    </div>

                    {/* Guide URL */}
                    {(selectedVariant.guide_url || selectedProductGuideUrl) && (
                      <div className="bg-amber-50 dark:bg-slate-900 rounded-lg p-4 mb-4 border border-amber-100 dark:border-slate-700">
                        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                          <ExternalLink className="h-4 w-4" />
                          <a
                            href={selectedVariant.guide_url || selectedProductGuideUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold hover:underline"
                          >
                            Xem hướng dẫn sử dụng
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Quantity Selector */}
                    <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 mb-4">
                      {(() => {
                        const maxQuantity = getMaxQuantity()
                        return (
                          <>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Số lượng</label>
                            <div className="flex items-center justify-center gap-4">
                              <button
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                disabled={quantity <= 1}
                                className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <span className="text-2xl font-bold text-gray-900 dark:text-gray-100 w-12 text-center">{quantity}</span>
                              <button
                                onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                                disabled={quantity >= maxQuantity}
                                className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                          </>
                        )
                      })()}
                    </div>

                    {combosLoading ? (
                      <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 mb-4 text-sm text-gray-500 dark:text-gray-300">
                        Đang tải combo...
                      </div>
                    ) : (
                      (() => {
                        const activeCombos = (variantCombos || []).filter((c) => c.is_active)
                        if (activeCombos.length === 0) return null

                        return (
                          <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Combo theo điểm (tặng quà)</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Điểm: <span className="font-bold">{points}</span></div>
                            </div>

                            <div className="mt-3 space-y-2">
                              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                                <input
                                  type="radio"
                                  name="combo-choice"
                                  checked={!selectedComboId}
                                  onChange={() => {
                                    setSelectedComboId('')
                                    setSelectedComboQuantity(0)
                                  }}
                                />
                                <span>Không dùng combo</span>
                              </label>

                              {activeCombos
                                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                                .map((c) => {
                                  const req = Math.max(1, Number(c.required_points || 1))
                                  const max = Math.floor(points / req)
                                  const giftItems = (c.items || []).filter((it) => it.variant_id !== c.base_variant_id)
                                  const checked = selectedComboId === c.id

                                  return (
                                    <div key={c.id} className="border border-gray-200 dark:border-gray-800 rounded-lg p-3 bg-white/70 dark:bg-gray-950/20">
                                      <label className="flex items-start gap-2 cursor-pointer">
                                        <input
                                          type="radio"
                                          name="combo-choice"
                                          checked={checked}
                                          onChange={() => {
                                            setSelectedComboId(c.id)
                                            setSelectedComboQuantity(max > 0 ? 1 : 0)
                                          }}
                                        />
                                        <div className="min-w-0">
                                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{c.combo_name}</div>
                                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Điểm yêu cầu: <span className="font-bold">{req}</span> | Tối đa: <span className="font-bold">{max}</span></div>
                                        </div>
                                      </label>

                                      {giftItems.length > 0 && (
                                        <div className="mt-2 text-xs text-gray-700 dark:text-gray-200">
                                          <div className="font-semibold mb-1">Quà tặng:</div>
                                          <div className="space-y-1">
                                            {giftItems.map((it) => {
                                              const label = it.variant_short_name
                                                ? `${it.variant_name} (${it.variant_short_name})`
                                                : (it.variant_name || it.variant_id)
                                              return (
                                                <div key={it.id} className="flex items-center justify-between gap-2">
                                                  <span className="truncate">{label}</span>
                                                  <span className="font-semibold">× {it.quantity}</span>
                                                </div>
                                              )
                                            })}
                                          </div>
                                        </div>
                                      )}

                                      {checked && (
                                        <div className="mt-3 flex items-center justify-between gap-3">
                                          <div className="text-sm text-gray-700 dark:text-gray-200">Số lần áp dụng</div>
                                          <input
                                            type="number"
                                            min={0}
                                            max={max}
                                            value={selectedComboQuantity}
                                            onChange={(e) => setSelectedComboQuantity(Math.max(0, Math.min(max, Number(e.target.value))))}
                                            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
                                            disabled={max <= 0}
                                          />
                                        </div>
                                      )}

                                      {checked && max <= 0 && (
                                        <div className="mt-2 text-xs text-red-600">Bạn chưa đủ điểm để dùng combo này.</div>
                                      )}
                                    </div>
                                  )
                                })}
                            </div>
                          </div>
                        )
                      })()
                    )}

                    {/* Discount Breakdown Info */}
                    {(() => {
                      const { breakdown, integratedPercent } = computePercent(user, selectedVariant)
                      const variantDiscount = breakdown.variantDiscount
                      const rankDiscount = breakdown.rankDiscount
                      const ctvDiscount = breakdown.ctvDiscount || 0
                      const totalDiscount = integratedPercent

                      return (
                        <div className="bg-amber-50 dark:bg-slate-900 text-blue-800 dark:text-slate-200 px-4 py-3 rounded-lg mb-4 text-sm space-y-1">
                          {variantDiscount > 0 && (
                            <div className="flex justify-between items-center">
                              <span>Giảm giá gói:</span>
                              <span className="font-semibold">-{variantDiscount}%</span>
                            </div>
                          )}
                          {rankDiscount > 0 && (
                            <div className="flex justify-between items-center">
                              <span>Giảm giá hạng:</span>
                              <span className="font-semibold">-{rankDiscount}%</span>
                            </div>
                          )}
                          {ctvDiscount > 0 && (
                            <div className="flex justify-between items-center">
                              <span>Ưu đãi CTV:</span>
                              <span className="font-semibold">-{ctvDiscount}%</span>
                            </div>
                          )}
                          <div className="border-t border-blue-200 dark:border-slate-700 my-1 pt-1 flex justify-between items-center font-medium">
                            <span>Tổng giảm giá:</span>
                            <span className="font-bold text-blue-900 dark:text-amber-300">-{totalDiscount}%</span>
                          </div>
                          {totalDiscount > 0 && (
                            <div className="text-xs text-blue-700/80 dark:text-slate-300/80">
                              Áp dụng theo: {breakdown.selectedSource === 'product' ? 'khuyến mãi sản phẩm' : breakdown.selectedSource === 'user' ? 'ưu đãi thành viên' : 'không có'}
                            </div>
                          )}
                        </div>
                      )
                    })()}

                    {/* Pricing */}
                    <div className="bg-gradient-to-r from-gray-50 to-amber-50 dark:from-slate-900 dark:to-slate-800 rounded-lg p-4 mb-6 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-200">Đơn giá:</span>
                        <div className="text-right">
                          {(user?.rank && user.rank !== 'newbie') || !!user?.is_ctv ? (
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                              <span className="text-gray-400 dark:text-gray-500 line-through text-sm">
                                {(selectedVariant.price || 0).toLocaleString('vi-VN')}đ
                              </span>
                              {(() => {
                                const { integratedPercent } = computePercent(user, selectedVariant)
                                const totalDiscount = integratedPercent

                                return totalDiscount > 0 && (
                                  <span className="bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-300 px-2 py-0.5 rounded text-xs font-medium">
                                    -{totalDiscount}%
                                  </span>
                                )
                              })()}
                              <span className="font-bold text-amber-600 dark:text-amber-300">
                                {(getUnitPrice() || 0).toLocaleString('vi-VN')}đ
                              </span>
                            </div>
                          ) : (
                            <span className="font-bold text-amber-600 dark:text-amber-300">{(getUnitPrice() || 0).toLocaleString('vi-VN')}đ</span>
                          )}
                        </div>
                      </div>

                      {quantity > 1 && (
                        <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-slate-700">
                          <span className="text-gray-600 dark:text-gray-200">Số lượng:</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">× {quantity}</span>
                        </div>
                      )}

                      <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-slate-700">
                        <span className="font-semibold text-gray-900 dark:text-gray-100">Tổng thanh toán:</span>
                        <span className="text-2xl font-bold text-green-600">{(getTotalPrice() || 0).toLocaleString('vi-VN')}đ</span>
                      </div>

                      {user && (
                        <>
                          <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-slate-700">
                            <span className="text-gray-500 dark:text-gray-400 text-sm">Số dư hiện tại:</span>
                            <span className={`text-sm font-medium ${canAfford() ? 'text-green-600' : 'text-red-600'}`}>
                              {(user.balance || 0).toLocaleString('vi-VN')}đ
                              {!canAfford() && ' (không đủ)'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-500 dark:text-gray-400 text-sm">Số dư sau khi mua:</span>
                            <span className={`text-sm font-bold ${canAfford() ? 'text-green-600' : 'text-red-600'}`}>
                              {((user.balance || 0) - getTotalPrice()).toLocaleString('vi-VN')}đ
                            </span>
                          </div>
                        </>
                      )}

                    </div>

                    {/* Manual Delivery Warning */}
                    {selectedVariant.is_manual_delivery && (
                      <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg mb-6 text-sm border border-yellow-200">
                        <p className="font-semibold mb-1">⚠️ Lưu ý quan trọng:</p>
                        {selectedVariant.manual_delivery_note ? (
                          <span>{selectedVariant.manual_delivery_note}</span>
                        ) : (
                          <>
                            Sản phẩm này sẽ trả về <span className="font-bold">Mã đơn hàng</span>. Bạn vui lòng gửi mã này cho hỗ trợ khách hàng để nhận key sản phẩm.
                          </>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-4">
                      {user && (user.balance || 0) < getTotalPrice() ? (
                        <button
                          onClick={() => {
                            const amountNeeded = Math.max(10000, getTotalPrice() - (user.balance || 0))
                            navigate(`/topup?amount=${amountNeeded}`)
                            closeModal()
                          }}
                          className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-3 px-4 rounded-lg font-semibold shadow-lg flex items-center justify-center gap-2 transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                          Nạp tiền ngay (Thiếu {(getTotalPrice() - (user.balance || 0)).toLocaleString('vi-VN')}đ)
                        </button>
                      ) : (
                        <button
                          onClick={confirmPurchase}
                          disabled={purchasing || !canAfford()}
                          className="flex-1 bg-gradient-to-r from-amber-600 to-amber-700 text-white py-3 px-4 rounded-lg hover:from-amber-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg flex items-center justify-center gap-2"
                        >
                          {purchasing ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              Đang xử lý...
                            </>
                          ) : (
                            <>
                              <ShoppingCart className="h-4 w-4" />
                              Xác nhận mua ({(getTotalPrice() || 0).toLocaleString('vi-VN')}đ)
                            </>
                          )}
                        </button>
                      )}
                      <button
                        onClick={closeModal}
                        disabled={purchasing}
                        className="px-6 py-3 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 transition-colors disabled:opacity-50 font-medium dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                      >
                        Hủy
                      </button>
                    </div>
                  </>
                ) : (
                  /* Purchase Success Result */
                  <>
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="h-8 w-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Mua hàng thành công!</h3>
                      <p className="text-gray-500 mt-1">Cảm ơn bạn đã mua hàng</p>
                    </div>

                    {/* Guide URL in Result */}
                    {purchaseResult.guideUrl && (
                      <div className="bg-amber-50 rounded-lg p-4 mb-4 border border-amber-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-amber-700">
                            <ExternalLink className="h-4 w-4" />
                            <span className="font-semibold">Hướng dẫn sử dụng:</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={purchaseResult.guideUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-amber-600 hover:underline text-sm font-medium"
                            >
                              Mở link
                            </a>
                            <button
                              onClick={() => copyToClipboard(purchaseResult.guideUrl!, 'guide')}
                              className="text-gray-400 hover:text-amber-600 p-1"
                              title="Sao chép liên kết"
                            >
                              {copiedKey === 'guide' ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Keys List */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      {/* Manual Delivery Instruction */}
                      {selectedVariant?.is_manual_delivery && (
                        <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg mb-4 text-sm border border-yellow-200">
                          <p className="font-bold mb-1">👉 Hướng dẫn nhận hàng:</p>
                          {selectedVariant?.manual_delivery_note ? (
                            <span>{selectedVariant?.manual_delivery_note}</span>
                          ) : (
                            <>
                              Hãy gửi mã giao dịch bên dưới cho CSKH (Chat Support ở góc màn hình) để nhận sản phẩm bạn nhé!
                            </>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-2 mb-3">
                        <Key className="h-4 w-4 text-purple-600" />
                        <span className="font-semibold text-gray-900">
                          Key của bạn ({purchaseResult.keys.length}):
                        </span>
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {purchaseResult.keys.map((key, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200"
                          >
                            <code className="text-sm font-mono text-gray-800 break-all flex-1">{key}</code>
                            <button
                              onClick={() => copyToClipboard(key, `key-${index}`)}
                              className="ml-2 text-gray-400 hover:text-amber-600 p-1 flex-shrink-0"
                              title="Sao chép key"
                            >
                              {copiedKey === `key-${index}` ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        ))}
                      </div>

                      {purchaseResult.giftKeys && purchaseResult.giftKeys.length > 0 && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Key className="h-4 w-4 text-purple-600" />
                            <span className="font-semibold text-gray-900">
                              Quà tặng ({purchaseResult.giftKeys.length}):
                            </span>
                          </div>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {purchaseResult.giftKeys.map((key, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200"
                              >
                                <code className="text-sm font-mono text-gray-800 break-all flex-1">{key}</code>
                                <button
                                  onClick={() => copyToClipboard(key, `gift-${index}`)}
                                  className="ml-2 text-gray-400 hover:text-amber-600 p-1 flex-shrink-0"
                                  title="Sao chép"
                                >
                                  {copiedKey === `gift-${index}` ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <Copy className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Copy All Keys */}
                      {purchaseResult.keys.length > 0 && (
                        <button
                          onClick={() => {
                            const textToCopy = purchaseResult.keys.map(key =>
                              `${key} - ${purchaseResult.productName || selectedProductName} - ${purchaseResult.variantName || selectedVariant?.name}`
                            ).join(', ')
                            copyToClipboard(textToCopy, 'all-keys')
                          }}
                          className="mt-3 w-full py-2 text-sm font-medium text-amber-600 hover:text-blue-800 flex items-center justify-center gap-2"
                        >
                          {copiedKey === 'all-keys' ? (
                            <>
                              <Check className="h-4 w-4 text-green-500" />
                              Đã sao chép tất cả!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              Sao chép tất cả keys
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {purchaseResult.manualDelivery ? (
                      <button
                        onClick={handleSupportChat}
                        className="w-full bg-gradient-to-r from-amber-600 to-amber-700 text-white py-3 px-4 rounded-lg hover:from-amber-700 hover:to-blue-800 transition-all font-semibold shadow-lg"
                      >
                        Gửi mã giao dịch đến hỗ trợ
                      </button>
                    ) : (
                      <button
                        onClick={closeModal}
                        className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-semibold"
                      >
                        Đóng
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      </div>
    </div>
  )
}
